import type { ChainIdentifier } from '@morpho-org/dapp-core'
import type { SearchParams } from 'nuqs'
import type { Address } from 'viem'

import slugifier from 'slugify'

import type { NavSegmentOption } from '@/components/common/NavSegmentControl'
import type { MarketSubTabs } from '@/components/MarketPage/MarketTabs/useMarketSubTabState'

import { getFixedRateAppUrl } from '@/config/external-urls'
import { isMultiChainSection } from '@/helpers/routeType'

/**
 * Slugifies a string by removing special characters and replacing spaces with hyphens.
 * @param string - String to slugify
 * @returns Slugified string in lowercase
 */
const _slugify = (string: string): string => {
  return slugifier(string, {
    remove: /[^a-zA-Z0-9 -]/g,
    strict: true,
    trim: true,
    lower: true
  })
}

/**
 * Generates a URL-safe slug from a vault name.
 * @param vaultName - Name of the vault
 * @returns URL-safe slug
 */
export const getVaultSlug = (vaultName: string) => {
  return _slugify(vaultName)
}

/**
 * The canonical vault detail path — the one the route resolver redirects to, the
 * `<link rel="canonical">` points at, and the sitemap lists.
 */
export const getVaultCanonicalPath = ({
  chainIdentifier,
  address,
  name
}: {
  chainIdentifier: ChainIdentifier
  address: Address
  name: string
}) => `/${chainIdentifier}/vault/${address}/${getVaultSlug(name)}`

export enum VaultPageTabValues {
  Vault = 'vault',
  MyPosition = 'myPosition',
  ManagePosition = 'managePosition'
}

export type VaultSubTabValues = 'yourPosition' | 'overview' | 'performance' | 'risk' | 'activity'

/**
 * Get the URL for the vaults listing page with optional filters.
 * Multi-chain page — no chain identifier in URL.
 */
export const getVaultsUrl = (opts?: {
  assetIdsFilter?: string[]
  curatorAddressesFilter?: string
}): string => {
  const baseUrl = '/vaults'
  const queryParams = new URLSearchParams({
    ...(opts?.assetIdsFilter &&
      opts.assetIdsFilter.length > 0 && {
        assetIdsFilter: opts.assetIdsFilter.join(',')
      }),
    ...(opts?.curatorAddressesFilter && {
      curatorAddressesFilter: opts.curatorAddressesFilter
    })
  })

  return queryParams.toString() ? `${baseUrl}?${queryParams}` : baseUrl
}

/**
 * Get the URL for the variable-rate markets listing page.
 * Multi-chain page — no chain identifier in URL.
 * The section was renamed from "Markets" (`/markets`) to "Variable" (`/variable`).
 */
export const getMarketsUrl = (): string => {
  return '/variable'
}

/**
 * Get the URL of a vault
 * @param identifier - Identifier of the chain
 * @param vaultAddress - Address of the vault
 * @param vaultName - Name of the vault
 * @param hash - Optional section ID to scroll to via URL hash (e.g. #risk)
 * @param mobileTab - Optional tab name to show for mobile devices
 */
export const getVaultUrl = ({
  identifier,
  vaultAddress,
  vaultName,
  hash,
  mobileTab
}: {
  identifier: ChainIdentifier
  vaultAddress: Address
  vaultName: string
  hash?: VaultSubTabValues
  mobileTab?: VaultPageTabValues
}) => {
  const baseUrl = `/${identifier}/vault/${vaultAddress}/${getVaultSlug(vaultName)}`

  const queryParams = new URLSearchParams()

  if (mobileTab) {
    queryParams.append('tab', mobileTab)
  }
  const url = queryParams.toString() ? `${baseUrl}?${queryParams}` : baseUrl
  return hash ? `${url}#${hash}` : url
}

/**
 * Generates a URL-safe slug from market loan and collateral symbols.
 *
 * Loan-first, matching the market name (see `getMarketName`). Stale
 * collateral-first URLs self-heal: the market page recomputes the canonical slug
 * and `permanentRedirect`s when the incoming one differs.
 *
 * @param loanSymbol - Symbol of the loan token
 * @param collateralSymbol - Symbol of the collateral token
 * @returns URL-safe slug in format "loan-collateral"
 */
export const getMarketSlug = ({
  loanSymbol,
  collateralSymbol
}: {
  loanSymbol: string
  collateralSymbol: string
}) => {
  return _slugify(`${loanSymbol.trim()}-${collateralSymbol.trim()}`)
}

/**
 * The canonical market detail path — the one the route resolver redirects to, the
 * `<link rel="canonical">` points at, and the sitemap lists. Idle markets pass an
 * empty `collateralSymbol` and get the loan-only slug.
 */
export const getMarketCanonicalPath = ({
  chainIdentifier,
  marketId,
  loanSymbol,
  collateralSymbol
}: {
  chainIdentifier: ChainIdentifier
  marketId: string
  loanSymbol: string
  collateralSymbol: string
}) => `/${chainIdentifier}/variable/${marketId}/${getMarketSlug({ loanSymbol, collateralSymbol })}`

/**
 * Serializes a page's resolved `searchParams` back into a query string.
 *
 * A server-side redirect that rebuilds only the pathname silently drops the caller's
 * query, so any redirect that wants to keep it has to re-serialize by hand. The URL
 * fragment is unrecoverable here — browsers never send it to the server.
 *
 * @param searchParams - The resolved `searchParams` of a page.
 * @returns The query string with no leading `?`, or an empty string when there is none.
 * @internal
 */
export const toQueryString = (searchParams: SearchParams): string => {
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(searchParams)) {
    if (Array.isArray(value)) {
      value.forEach(entry => params.append(key, entry))
    } else if (value !== undefined) {
      params.append(key, value)
    }
  }

  return params.toString()
}

export enum MarketPageTabValues {
  Market = 'market',
  MyPosition = 'myPosition',
  ManagePosition = 'managePosition'
}

/*
 * @dev - Get the URL of a market
 * @param identifier - Identifier of the chain
 * @param marketId - Unique key of the market
 * @param collateralSymbol - Symbol of the collateral
 * @param loanSymbol - Symbol of the loan
 * @param hash - Optional section ID to scroll to via URL hash (e.g. #risk)
 * @param mobileTab - page tab name to show for mobile devices
 * @returns - The market URL with query parameters and optional hash
 */
export const getMarketUrl = ({
  identifier,
  marketId,
  collateralSymbol,
  loanSymbol,
  hash,
  mobileTab
}: {
  identifier: ChainIdentifier
  marketId: string
  collateralSymbol: string
  loanSymbol: string
  hash?: MarketSubTabs
  mobileTab?: MarketPageTabValues
}) => {
  const baseUrl = `/${identifier}/variable/${marketId}/${getMarketSlug({
    loanSymbol,
    collateralSymbol
  })}`

  const queryParams = new URLSearchParams()

  if (mobileTab) {
    queryParams.append('tab', mobileTab)
  }
  const url = queryParams.toString() ? `${baseUrl}?${queryParams}` : baseUrl
  return hash ? `${url}#${hash}` : url
}

/**
 * Constructs the URL for a curator page.
 * Multi-chain page — no chain identifier in URL.
 */
export const getCuratorUrl = ({ curatorId }: { curatorId: string }) => {
  return `/curator/${curatorId}`
}

/**
 * Constructs the URL for a user's portfolio page.
 * Multi-chain page — no chain identifier in URL.
 */
export const getPortfolioUrl = ({ userAddress }: { userAddress: Address }) => {
  return `/portfolio/${userAddress}`
}

export const NAVIGATION_VALUE_VAULTS = 'vaults'
export const NAVIGATION_VALUE_MARKETS = 'variable'
export const NAVIGATION_VALUE_FIXED_RATE = 'fixedRate'
export const NAVIGATION_VALUE_PORTFOLIO = 'portfolio'

interface NavigationOption extends NavSegmentOption<string> {
  isMobileSubNavigation?: boolean
}

/*
 * @dev - Get the navigation options
 * @param positionCount - When > 0, the Portfolio option renders a count badge so
 *        the user can tell at a glance how many positions they hold without
 *        opening the page.
 * @param userAddress - When set (wallet connected), the Portfolio tab links
 *        straight to /portfolio/<address> so we skip the bare /portfolio redirect
 *        hop, which otherwise briefly flashes the connect/empty state. When unset
 *        (disconnected), the tab carries no href so the nav layer can intercept
 *        the click and open the connect modal (see usePortfolioConnectGate)
 *        instead of landing on the bare /portfolio connect screen.
 * @returns - Array of navigation options
 *
 * Each `labelWidth` is the widest the label sets in Inter or its fallback, at the desktop (13px)
 * and mobile (12px) tab sizes, with and without font hinting, so a late Inter swap can't slide
 * the tabs after it. Re-measure when a label changes: the late-font E2E (Chromium, 1440px desktop
 * and 390px mobile) fails when one overflows in either face. The vertical burger menu ignores it.
 */
export const getNavigationOptions = ({
  positionCount = 0,
  userAddress
}: {
  positionCount?: number
  userAddress?: Address
} = {}): NavigationOption[] => {
  return [
    {
      label: 'Vaults',
      labelWidth: '3em',
      value: NAVIGATION_VALUE_VAULTS,
      href: getVaultsUrl(),
      prefetch: true,
      isMobileSubNavigation: true
    },
    {
      // "Markets" is spelled out so the Vaults / Markets split stays clear next
      // to "Fixed Rate Markets ↗". URL stays /variable (legacy /markets
      // redirects in proxy.ts).
      label: 'Variable Rate Markets',
      labelWidth: '10.84em',
      value: NAVIGATION_VALUE_MARKETS,
      href: getMarketsUrl(),
      prefetch: true,
      isMobileSubNavigation: true
    },
    {
      // `external` opens the Fixed Rate app in a new tab with the 45° arrow; the
      // href carries UTM attribution for referral tracking (getFixedRateAppUrl).
      // Not `isMobileSubNavigation`: an external link can't be a switch-button, so
      // on mobile it lives in the burger menu only.
      label: 'Fixed Rate Markets',
      labelWidth: '9.59em',
      value: NAVIGATION_VALUE_FIXED_RATE,
      href: getFixedRateAppUrl('topnav'),
      external: true
    },
    {
      // `counter` is consumed by `NavSegmentControl` to render the position-count
      // badge next to the label (only when > 0).
      label: 'Portfolio',
      labelWidth: '4.34em',
      value: NAVIGATION_VALUE_PORTFOLIO,
      // Connected: link straight to the address-scoped Portfolio. Disconnected:
      // no href, so the nav layer intercepts the click to open the connect modal
      // instead of routing to the bare /portfolio connect screen (an empty state
      // we'd rather not show). See usePortfolioConnectGate.
      href: userAddress ? getPortfolioUrl({ userAddress }) : undefined,
      // Without this the route only prefetches on hover, which never happens on
      // touch screens, so a mobile tap waits on the network before anything changes.
      prefetch: true,
      isMobileSubNavigation: true,
      counter: positionCount
    }
  ]
}

/**
 * Whether the mobile sub-navigation belongs on a given pathname.
 *
 * The sub-nav only shows on top-level (multi-chain) section pages — /vaults,
 * /variable, /portfolio — never on chain-scoped detail pages like
 * /[chain]/variable/[id]. Match the FIRST path segment EXACTLY against the
 * sub-nav values rather than a substring `includes`: since the markets→variable
 * rename, the detail route (/[chain]/variable/...) shares the "variable" nav
 * value, so a substring check would wrongly light up the sub-nav on those detail
 * pages (the old singular /[chain]/market/... never matched the plural "markets").
 * @param pathname - Current URL pathname
 * @param subNavigationValues - The nav values shown in the mobile sub-navigation
 * @returns Whether the sub-navigation should render on this pathname
 */
export const isSubNavigationPathname = (
  pathname: string,
  subNavigationValues: string[]
): boolean => {
  const firstSegment = pathname.split('/').filter(Boolean)[0] ?? ''
  return subNavigationValues.includes(firstSegment)
}

/**
 * Extracts the navigation value from a URL pathname.
 * Handles both multi-chain URLs (/vaults, /variable) and chain-specific (/ethereum/vault/...).
 */
export const getNavigationValueFromPathname = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean)
  const firstSegment = segments[0]

  // Multi-chain pages: first segment is the section
  if (firstSegment && isMultiChainSection(firstSegment)) {
    return firstSegment
  }

  // Chain-specific pages: section is at index 1
  const section = segments[1]
  // Map detail page sections to their listing page nav values
  if (section === 'vault') return NAVIGATION_VALUE_VAULTS
  if (section === 'variable') return NAVIGATION_VALUE_MARKETS
  return section || '/'
}
